export type PermissionKey =
  | 'sceneVisible' | 'lgsxUpload' | 'matterportVisible' | 'releaseNotesVisible' | 'gaussianVisible'
  | 'gaussianGenerate' | 'orthomosaicVisible' | 'video360Visible' | 'basemapVisible' | 'advancedTourVisible';

export type Permissions = Record<PermissionKey, boolean>;

// Every per-user feature switch, in the order the admin pages list them
// (Edit permissions, and the switches a new registration starts with).
export const PERMISSIONS_META: { key: PermissionKey; label: string; description: string }[] = [
  {
    key: 'sceneVisible',
    label: 'Scene',
    description: 'Open the Scene: point clouds, 360° panoramas, BIM models and drawings in one 3D view. Splats and orthomosaics appear in it only with their own permissions below',
  },
  {
    key: 'lgsxUpload',
    label: 'LGSX file support',
    description: 'Allow uploading Leica .lgsx files to Point Cloud — hidden from the supported formats otherwise',
  },
  {
    key: 'matterportVisible',
    label: 'Matterport tour',
    description: 'Access to Matterport tour links on projects — view them, and set the link on owned projects',
  },
  {
    key: 'releaseNotesVisible',
    label: 'Visible release notes',
    description: 'See the release-notes icon in the top bar and open the release notes pages',
  },
  {
    key: 'gaussianVisible',
    label: 'Gaussian splats',
    description: 'Show the Gaussian Splats project section and allow uploading trained splat files (.ply) generated elsewhere',
  },
  {
    key: 'gaussianGenerate',
    label: 'Allow generate Gaussian splats',
    description: 'Generate splats from scans or converted point clouds (GPU training, limited per month by the user plan). Requires the Gaussian splats section permission to be useful',
  },
  {
    key: 'orthomosaicVisible',
    label: 'Orthomosaic images',
    description: 'Show the Orthomosaic project section and allow uploading and viewing drone-survey GeoTIFF orthomosaics',
  },
  {
    key: 'video360Visible',
    label: '360 videos',
    description: 'Show the 360 Videos project section and the videos in the Walkthroughs menu, and allow uploading equirectangular MP4/MOV videos',
  },
  {
    key: 'basemapVisible',
    label: 'Base map in the Scene',
    description: 'Show the Base map option in the Scene and draw the satellite or street map under the project (also on this user\'s share links)',
  },
  {
    key: 'advancedTourVisible',
    label: 'Advanced Virtual Tour',
    description: 'In the Virtual Tour: the scan areas on the minimap, a compass with beacons that point to the other areas, and the tour map; name the areas in the Scene (also on this user\'s share links)',
  },
];

export const NO_PERMISSIONS: Permissions = Object.fromEntries(PERMISSIONS_META.map((p) => [p.key, false])) as Permissions;

// Exactly the known switches, each a boolean (GraphQL objects carry __typename).
export const permissionsOf = (from: Partial<Record<string, unknown>> | null | undefined): Permissions =>
  Object.fromEntries(PERMISSIONS_META.map((p) => [p.key, from?.[p.key] === true])) as Permissions;
